'use client';

import { captureException } from '@sentry/react';
import { ErrorResponse, Log, SignoutResponse, UserManager, UserManagerSettings, WebStorageStateStore } from 'oidc-client-ts';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { AuthProvider as OIDCAuthProvider } from 'react-oidc-context';

const isOktaProvider = process.env.AUTH_CLIENT_PROVIDER === 'okta';

const ORG_LEADER_CLIENT_ID = 'cribl-spa';

export const PRIMARY_AUTH_ISSUER = process.env.AUTH_CLIENT_ISSUER || `https://${process.env.AUTH_CLIENT_DOMAIN}`;

const OCI_ORG_CONFIG_KEY = 'oci_org_config';
const AUTH_CALLBACK_MODE_KEY = 'auth_callback_mode';
const DISCOVERY_TIMEOUT_MS = 10_000;

type OciOrgConfig = {
  authBaseUrl: string;
};

export type AuthMode =
  { kind: 'primary' } |
  { kind: 'orgLeader'; authBaseUrl: string; authority: string };

function readOciOrgConfig(): OciOrgConfig | null {
  try {
    const raw = localStorage.getItem(OCI_ORG_CONFIG_KEY);

    if (!raw) {
      return null;
    }

    const parsed = JSON.parse(raw) as OciOrgConfig;

    if (!parsed.authBaseUrl || typeof parsed.authBaseUrl !== 'string') {
      return null;
    }

    new URL(parsed.authBaseUrl);

    return parsed;
  } catch {
    localStorage.removeItem(OCI_ORG_CONFIG_KEY);

    return null;
  }
}

function persistOciOrgConfig(authBaseUrl: string): void {
  localStorage.setItem(OCI_ORG_CONFIG_KEY, JSON.stringify({ authBaseUrl }));
}

function clearOciOrgConfig(): void {
  localStorage.removeItem(OCI_ORG_CONFIG_KEY);
}

function isAbortError(error: unknown): boolean {
  if (error instanceof DOMException && error.name === 'AbortError') {
    return true;
  }

  return error instanceof Error && error.name === 'AbortError';
}

function isPostLogoutRedirect(): boolean {
  return window.location.pathname === '/logout' &&
    new URLSearchParams(window.location.search).has('state');
}

function handleSignoutCallback(resp: SignoutResponse | undefined): void {
  // userState shape set by CommonAuthProvider.logout; unknown from oidc-client-ts
  const returnTo = (resp?.userState as { returnTo?: string } | undefined)?.returnTo;
  // Remove ?state= from history so back-button lands on clean /logout instead of a stale state URL
  window.history.replaceState({}, '', '/logout');
  window.location.assign(returnTo || (process.env.AUTH_CLIENT_LOGOUT_URI as string));
}

/** Skip discovery while OAuth callback is exchanging ?code= for tokens. */
function isActiveOAuthCallback(): boolean {
  if (typeof window === 'undefined') {
    return false;
  }

  if (window.location.pathname !== '/callback') {
    return false;
  }

  const params = new URLSearchParams(window.location.search);

  return params.has('code') || params.has('error');
}

function createPrimaryUserManager(): UserManager {
  const settings: UserManagerSettings = {
    authority: PRIMARY_AUTH_ISSUER,
    client_id: process.env.AUTH_CLIENT_ID as string,
    redirect_uri: `${window.location.origin}/callback`,
    post_logout_redirect_uri: `${window.location.origin}/logout`,
    scope: process.env.AUTH_CLIENT_SCOPE,
    // For Gov environments (they use OKTA), we use session storage
    userStore: new WebStorageStateStore({ store: isOktaProvider ? window.sessionStorage : window.localStorage }),
    loadUserInfo: true,
    automaticSilentRenew: true,
    response_type: 'code',
    extraQueryParams: { audience: process.env.AUTH_CLIENT_AUDIENCE_BROWSER as string },
  };

  return new UserManager(settings);
}

function orgLeaderAuthority(authBaseUrl: string): string {
  return `${authBaseUrl.replace(/\/$/, '')}/oidc`;
}

function createOrgLeaderUserManager(authBaseUrl: string): UserManager {
  const authority = orgLeaderAuthority(authBaseUrl);

  return new UserManager({
    authority,
    client_id: ORG_LEADER_CLIENT_ID,
    redirect_uri: `${window.location.origin}/callback`,
    scope: 'openid profile email',
    response_type: 'code',
    userStore: new WebStorageStateStore({ store: window.localStorage }),
    loadUserInfo: false,
  });
}

function createManagerForAuthMode(authMode: AuthMode): UserManager {
  if (authMode.kind === 'orgLeader') {
    return createOrgLeaderUserManager(authMode.authBaseUrl);
  }

  return createPrimaryUserManager();
}

function authModeAuthority(authMode: AuthMode): string {
  return authMode.kind === 'orgLeader' ? authMode.authority : PRIMARY_AUTH_ISSUER;
}

function initialAuthMode(): AuthMode {
  if (typeof window === 'undefined') {
    return { kind: 'primary' };
  }

  const sessionMode = sessionStorage.getItem(AUTH_CALLBACK_MODE_KEY);

  if (sessionMode === 'primary') {
    return { kind: 'primary' };
  }

  const config = readOciOrgConfig();

  if (!config) {
    return { kind: 'primary' };
  }

  const authBaseUrl = config.authBaseUrl;

  return { kind: 'orgLeader', authBaseUrl, authority: orgLeaderAuthority(authBaseUrl) };
}

type OAuthProviderContextType = {
  getManager: () => UserManager | null;
  switchToOrgLeader: (authBaseUrl: string) => void;
  switchToPrimary: () => void;
};

const OAuthProviderContext = React.createContext<OAuthProviderContextType>({
  getManager: () => null,
  switchToOrgLeader: () => { throw new Error('Context not initialized'); },
  switchToPrimary: () => {},
});

export const OAuthProvider: React.FC<React.PropsWithChildren> = ({ children }) => {
  const [authMode, setAuthMode] = useState<AuthMode>(initialAuthMode);
  const authModeRef = useRef<AuthMode>(authMode);
  const managerRef = useRef<UserManager | null>(null);
  const authorityRef = useRef(authModeAuthority(authMode));
  const discoveryGenerationRef = useRef(0);

  authModeRef.current = authMode;

  const activeAuthority = authModeAuthority(authMode);

  if (typeof window !== 'undefined' && (!managerRef.current || authorityRef.current !== activeAuthority)) {
    managerRef.current?.stopSilentRenew();
    managerRef.current = createManagerForAuthMode(authMode);
    authorityRef.current = activeAuthority;
  }

  const getManager = useCallback(() => managerRef.current, []);

  const switchToOrgLeader = useCallback((authBaseUrl: string): void => {
    const authority = orgLeaderAuthority(authBaseUrl);

    sessionStorage.setItem(AUTH_CALLBACK_MODE_KEY, 'orgLeader');
    persistOciOrgConfig(authBaseUrl);

    managerRef.current?.stopSilentRenew();
    managerRef.current = createOrgLeaderUserManager(authBaseUrl);
    authorityRef.current = authority;

    const nextMode: AuthMode = { kind: 'orgLeader', authBaseUrl, authority };
    authModeRef.current = nextMode;
    setAuthMode(nextMode);
  }, []);

  const switchToPrimary = useCallback(() => {
    sessionStorage.setItem(AUTH_CALLBACK_MODE_KEY, 'primary');

    if (authModeRef.current.kind === 'primary') {
      return;
    }

    if (managerRef.current) {
      managerRef.current.removeUser().catch(() => {});
    }

    managerRef.current?.stopSilentRenew();
    managerRef.current = createPrimaryUserManager();
    authorityRef.current = PRIMARY_AUTH_ISSUER;
    clearOciOrgConfig();
    authModeRef.current = { kind: 'primary' };
    setAuthMode({ kind: 'primary' });
  }, []);

  useEffect(() => {
    const manager = managerRef.current;

    if (!manager) {
      return;
    }

    Log.setLogger(console);
    Log.setLevel(Log.WARN);

    const handleSilentRenewError = (error: Error) => {
      const oauthError = error instanceof ErrorResponse ? error.error : error.message;
      const isSessionGone = oauthError === 'login_required' ||
        oauthError === 'consent_required' ||
        oauthError === 'interaction_required';

      if (isSessionGone) {
        manager.stopSilentRenew();
        manager.removeUser();

        if (authModeRef.current.kind === 'orgLeader') {
          captureException(oauthError, { tags: { event: 'silentRenewError', authority: authModeRef.current.authority } });
          clearOciOrgConfig();
          managerRef.current?.stopSilentRenew();
          managerRef.current = createPrimaryUserManager();
          authorityRef.current = PRIMARY_AUTH_ISSUER;
          authModeRef.current = { kind: 'primary' };
          setAuthMode({ kind: 'primary' });
        }
      }
    };

    manager.events.addSilentRenewError(handleSilentRenewError);

    return () => {
      manager.events.removeSilentRenewError(handleSilentRenewError);
    };
  }, [authMode.kind]);

  // Validate org leader discovery is reachable; fall back to primary on failure.
  useEffect(() => {
    if (authMode.kind !== 'orgLeader' || !managerRef.current || isActiveOAuthCallback()) {
      return;
    }

    discoveryGenerationRef.current += 1;
    const generation = discoveryGenerationRef.current;
    const controller = new AbortController();
    let discoveryAbortReason: 'cleanup' | 'timeout' | null = null;
    const timeout = setTimeout(() => {
      discoveryAbortReason = 'timeout';
      controller.abort();
    }, DISCOVERY_TIMEOUT_MS);
    const authority = authMode.authority;

    fetch(`${authority}/.well-known/openid-configuration`, { signal: controller.signal })
      .then((res) => {
        if (!res.ok) {
          throw new Error(`OIDC discovery failed for ${authority}: ${res.status}`);
        }
      })
      .catch((error: unknown) => {
        if (generation !== discoveryGenerationRef.current) {
          return;
        }

        if (isAbortError(error) && discoveryAbortReason !== 'timeout') {
          return;
        }

        captureException(error, { tags: { authority } });
        clearOciOrgConfig();
        managerRef.current?.stopSilentRenew();
        managerRef.current = createPrimaryUserManager();
        authorityRef.current = PRIMARY_AUTH_ISSUER;
        authModeRef.current = { kind: 'primary' };
        setAuthMode({ kind: 'primary' });
      })
      .finally(() => clearTimeout(timeout));

    return () => {
      clearTimeout(timeout);
      discoveryAbortReason = 'cleanup';
      controller.abort();
    };
  }, [authMode]);

  const context: OAuthProviderContextType = useMemo(() => ({
    getManager,
    switchToOrgLeader,
    switchToPrimary,
  }), [getManager, switchToOrgLeader, switchToPrimary]);

  if (!managerRef.current) {
    return null;
  }

  return (
    <OAuthProviderContext.Provider value={context}>
      <OIDCAuthProvider
        key={activeAuthority}
        userManager={managerRef.current}
        skipSigninCallback
        matchSignoutCallback={isPostLogoutRedirect}
        onSignoutCallback={handleSignoutCallback}
      >
        {children}
      </OIDCAuthProvider>
    </OAuthProviderContext.Provider>
  );
};

export const useOAuthProvider = (): OAuthProviderContextType => React.useContext(OAuthProviderContext);
